/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import Loading from '@/components/Loading'
import {
  useOwnerSettingsQuery,
  useUpdateOwnerSettingsMutation,
} from '@/hooks/useOwnerSettings'
import { useCallback, useMemo } from 'react'

const TZ_OPTIONS = [
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Dakar',
  'Africa/Casablanca',
  'Africa/Lagos',
  'Europe/Paris',
]

export default function DigestSettingsCard() {
  const { data: settings, status } = useOwnerSettingsQuery(true)
  const updateSettings = useUpdateOwnerSettingsMutation()

  const digestEnabled = settings?.digestEnabled ?? true
  const digestHour = settings?.digestHour ?? '21:00'
  const tz = settings?.tz ?? 'Africa/Abidjan'

  const onToggle = useCallback(
    async (checked: boolean) => {
      try {
        await updateSettings.mutateAsync({ digestEnabled: checked })
        toast.success(checked ? 'Digest activé' : 'Digest désactivé')
      } catch (e: any) {
        toast.error("Impossible d'enregistrer", {
          description: e?.response?.data?.message ?? 'Erreur inconnue',
        })
      }
    },
    [updateSettings]
  )

  const onHourBlur = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value // "HH:MM"
      if (!/^\d{2}:\d{2}$/.test(value)) return
      try {
        await updateSettings.mutateAsync({ digestHour: value })
        toast.success('Heure du digest mise à jour')
      } catch (e: any) {
        toast.error("Impossible d'enregistrer", {
          description: e?.response?.data?.message ?? 'Erreur inconnue',
        })
      }
    },
    [updateSettings]
  )

  const onTzChange = useCallback(
    async (val: string) => {
      try {
        await updateSettings.mutateAsync({ tz: val })
        toast.success('Fuseau horaire mis à jour')
      } catch (e: any) {
        toast.error("Impossible d'enregistrer", {
          description: e?.response?.data?.message ?? 'Erreur inconnue',
        })
      }
    },
    [updateSettings]
  )

  const tzItems = useMemo(
    () =>
      TZ_OPTIONS.map((opt) => (
        <SelectItem value={opt} key={opt}>
          {opt}
        </SelectItem>
      )),
    []
  )

  return (
    <Card>
      <CardHeader>
        <h2 className='text-xl font-semibold'>Récap quotidien (digest)</h2>
        <p className='text-sm text-muted-foreground'>
          Recevez chaque soir un email avec vos ventes, encaissements, dettes et
          top articles.
        </p>
      </CardHeader>
      <CardContent>
        {status === 'pending' ? (
          <Loading />
        ) : (
          <div className='space-y-5'>
            <div className='flex items-center justify-between'>
              <div>
                <Label htmlFor='digest-enabled'>
                  Activer le digest quotidien
                </Label>
                <div className='text-xs text-muted-foreground'>
                  Envoi à l’heure locale choisie.
                </div>
              </div>
              <Switch
                id='digest-enabled'
                checked={!!digestEnabled}
                onCheckedChange={onToggle}
              />
            </div>

            <Separator />

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='digest-hour'>Heure d’envoi</Label>
                <input
                  key={digestHour}
                  id='digest-hour'
                  type='time'
                  step={300}
                  className='h-9 rounded-md border bg-background px-3 text-sm'
                  defaultValue={digestHour}
                  onBlur={onHourBlur}
                  disabled={!digestEnabled}
                />
                <span className='text-xs text-muted-foreground'>
                  Format 24h (ex: 21:00). Par défaut: 21:00.
                </span>
              </div>

              <div className='flex flex-col gap-2'>
                <Label>Fuseau horaire</Label>
                <Select
                  onValueChange={onTzChange}
                  defaultValue={tz}
                  disabled={!digestEnabled}
                >
                  <SelectTrigger className='h-9'>
                    <SelectValue placeholder='Choisir un fuseau' />
                  </SelectTrigger>
                  <SelectContent>{tzItems}</SelectContent>
                </Select>
                <span className='text-xs text-muted-foreground'>
                  Par défaut : Africa/Abidjan.
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
